import React, { useEffect, useState } from 'react';
import { Modal, Button, Select, Input, Checkbox, Flex, Typography, Alert, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAppSelector } from 'store/hook';
import { getTheme } from 'helpers/getTheme';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import RowLoader from 'components/Sceletons/RowLoader';
import { IPaymentHistoryItem, IRefundCalculation } from 'interfaces';
import billing from 'api/billing';
import classes from './RefundModal.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: IPaymentHistoryItem | null;
  onSuccess?: () => void;
}

const RefundModal: React.FC<RefundModalProps> = ({
  isOpen,
  onClose,
  record,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { userData } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const [isCheckLoading, setIsCheckLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRefundStatus, setExistingRefundStatus] = useState<string | null>(null);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [comment, setComment] = useState<string>('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [refundInfo, setRefundInfo] = useState<IRefundCalculation | null>(null);

  useEffect(() => {
    if (isOpen && record) {
      setReason(undefined);
      setComment('');
      setIsConfirmed(false);
      setExistingRefundStatus(null);
      checkExistingRefund(record.id);
    }
  }, [isOpen, record]);

  const checkExistingRefund = async (tariffHistoryId: string) => {
    setIsCheckLoading(true);
    try {
      const { data } = await billing.checkRefund(tariffHistoryId);
      if (data?.result) {
        setExistingRefundStatus(data.result);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        messageApi?.error(t('Billing.refundModal.checkError'));
      }
    } finally {
      setIsCheckLoading(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!record || !reason || !isConfirmed) return;

    setIsSubmitting(true);
    try {
      await billing.createRefund({
        tariff_history_id: record.id,
        reason,
        comment: comment.trim() || undefined,
      });
      messageApi?.success(t('Billing.refundModal.success'));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      messageApi?.error(err?.response?.data?.message || t('Billing.refundModal.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRefundStatusLabel = (result: string): string =>
    t(`Billing.refundStatus.${result}`, { defaultValue: result });

  const getRefundStatusColor = (result: string): string => {
    if (['S', 'R', 'finished'].includes(result)) return 'success';
    if (['E', 'J', 'F'].includes(result)) return 'error';
    if (['processing', 'r', 'W', 'w', 'N', 'n', 'I'].includes(result)) return 'warning';
    return 'default';
  };

  const reasonOptions = [
    { value: 'not_suitable', label: t('Billing.refundModal.reasons.not_suitable') },
    { value: 'error_payment', label: t('Billing.refundModal.reasons.error_payment') },
    { value: 'plan_change', label: t('Billing.refundModal.reasons.plan_change') },
  ];

  const paymentDate = record?.paid_at
    ? getDateWithTimeZone(record.paid_at, userData?.date_format || 'international')
    : record?.created_at
    ? getDateWithTimeZone(record.created_at, userData?.date_format || 'international')
    : '——';

  const tariffName =
    record?.tariff_name ||
    record?.quote?.tariff_plan_name ||
    '——';

  const hasExistingRefund = existingRefundStatus !== null;

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      title={t('Billing.refundModal.title')}
      width={480}
      footer={null}
      destroyOnClose
      className={classNames(classes.modal, getTheme())}
    >
      <Flex vertical gap={18}>
        <Flex vertical gap={10}>
          <Text strong>{t('Billing.refundModal.tariffData')}</Text>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.operationId')}</Text>
            <Text>{record?.id || '——'}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.paymentDate')}</Text>
            <Text>{paymentDate}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.tariff')}</Text>
            <Text>{tariffName}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.room')}</Text>
            <Text>{record?.room_name || '——'}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.originalAmount')}</Text>
            <Text>
              {record?.cost !== undefined
                ? `${record.cost.toLocaleString('ru-RU')} ₽`
                : '——'}
            </Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.paymentMethod')}</Text>
            <Text>{record?.payment_card || '——'}</Text>
          </Flex>
        </Flex>

        {/* TODO: разкоментить когда бекенд будет отдавать данные по расчету тарифа (refundInfo стейт, примерный интерфейс IRefundCalculation)
        <Flex vertical gap={10}>
          <Text strong>{t('Billing.refundModal.refundCalculation')}</Text>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.paidPeriod')}</Text>
            <Text>{refundInfo?.paid_period_days} {t('Billing.refundModal.days')}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.used')}</Text>
            <Text>{refundInfo?.used_days} {t('Billing.refundModal.days')}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.unused')}</Text>
            <Text>{refundInfo?.unused_days} {t('Billing.refundModal.days')}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text type="secondary">{t('Billing.refundModal.dayCost')}</Text>
            <Text>
              {refundInfo?.day_price !== undefined
                ? `${refundInfo.day_price.toLocaleString('ru-RU')} ₽`
                : '——'}
            </Text>
          </Flex>

          <Flex vertical gap={6} className={classes.highlightBox}>
            <Flex justify="space-between" align="center">
              <Text className={classes.highlightLabel}>{t('Billing.refundModal.refundAmount')}</Text>
              <Text className={classes.highlightValue}>
                {refundInfo?.refund_amount !== undefined
                  ? `${refundInfo.refund_amount.toLocaleString('ru-RU')} ₽`
                  : '0 ₽'}
              </Text>
            </Flex>
            <Text type="secondary" className={classes.highlightHint}>
              {t('Billing.refundModal.refundCalculationHint')}
            </Text>
          </Flex>
        </Flex>
        */}

        {isCheckLoading ? (
          <RowLoader height={40} width="100%" padding="8px 0" />
        ) : hasExistingRefund ? (
          <Alert
            type="info"
            message={t('Billing.refundModal.existingRefund')}
            description={
              <Flex align="center" gap={8}>
                <Text type="secondary">{t('Billing.refundModal.refundStatus')}:</Text>
                <Tag color={getRefundStatusColor(existingRefundStatus!)}>
                  {getRefundStatusLabel(existingRefundStatus!)}
                </Tag>
              </Flex>
            }
          />
        ) : (
          <>
            <Flex vertical gap={6}>
              <Text>{t('Billing.refundModal.refundReason')}</Text>
              <Select
                placeholder={t('Billing.refundModal.reasonPlaceholder')}
                value={reason}
                onChange={(val) => setReason(val)}
                options={reasonOptions}
              />
            </Flex>

            <Flex vertical gap={6}>
              <Text>{t('Billing.refundModal.comment')}</Text>
              <TextArea
                rows={3}
                placeholder={t('Billing.refundModal.commentPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Flex>

            <Checkbox
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
            >
              <Text>{t('Billing.refundModal.confirmCheckbox')}</Text>
            </Checkbox>

            <Flex justify="flex-end" align="center" gap={12} className={classes.footer}>
              <Button
                className={classes.cancelButton}
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('Billing.refundModal.cancel')}
              </Button>
              <Button
                type="primary"
                className={classes.submitButton}
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!isConfirmed || !reason}
              >
                {t('Billing.refundModal.submit')}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default RefundModal;
